// @flow
import React, { PureComponent } from 'react';
import TagManager from 'react-gtm-module';
import { Link, Redirect } from 'react-router-dom';

import ClubSearch from 'components/club/ClubSearch';
import CardThumbnail from 'components/fragments/card/CardThumbnail';
import { STATUS_SUCCESS } from 'constants/statusConstants';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { ClubPreviewType } from 'types/Club';
import type { BlocFaq } from 'types/Settings';
import type { Status } from 'types/Status';
import type { FullUserType } from 'types/User';
import { dynamicClassName } from 'utils/dynamicClassName';
import { getMetaBySlug } from 'utils/seoUtils';
import { handleTagEvent } from 'utils/tagManagerUtils';

export type StateProps = {
  blocs_trouver_club: BlocFaq[],
  clubs: ClubPreviewType[],
  featured_image: string,
  keycloakData: any,
  pratiques: string[],
  pratiques_ok: string[],
  searchCities: string[],
  searchClubs: { logo: string, name: string }[],
  searchTypes: string[],
  status: Status,
  userPref: FullUserType,
  userPrefStatus: Status
};

export type DispatchProps = {
  fetchClubsPreview: () => void
};

type Props = StateProps &
  DispatchProps & {
    ffrDomain?: string
  };

type State = {
  inputFocused: boolean,
  redirect: boolean,
  rugbyTypes: string,
  geolocalisation: boolean
};

class ClubFinder extends PureComponent<Props, State> {
  props: Props;
  state: State = {
    inputFocused: false,
    redirect: false,
    rugbyTypes: '',
    geolocalisation: false
  };

  componentDidMount() {
    const { userPrefStatus } = this.props;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          this.setState({ geolocalisation: true });
        },
        error => {
          console.warn(`ERREUR (${error.code}): ${error.message}`);
          this.setState({ geolocalisation: false });
        }
      );
    } else {
      this.setState({ geolocalisation: false });
    }

    if (userPrefStatus === STATUS_SUCCESS) {
      this.handleVPV();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.userPrefStatus !== this.props.userPrefStatus && this.props.userPrefStatus === STATUS_SUCCESS) {
      this.handleVPV();
    }
  }

  handleVPV = () => {
    const { userPref } = this.props;

    // Pour fix la VPV https://redmine.dev.ffr.fr/issues/8894?issue_count=113&issue_position=12&next_issue_id=8888&prev_issue_id=8900#note-23

    const userId = userPref.login ? userPref.login : '';
    const licensee = userPref.licence !== '' ? 'yes' : 'no';
    const licenseeClub = userPref.club_actuel.nom ? userPref.club_actuel.nom : '';
    const licenseeFunctions = userPref.licenceefunctions ? userPref.licenceefunctions : '';
    const newsletterThematic = Object.values(userPref.newsletter).join(', ');
    const newsletterSubscriber = newsletterThematic.length > 0 ? 'yes' : 'no';

    TagManager.dataLayer({
      dataLayer: {
        event: 'virtualPageview',
        virtualPageURL: window.location.pathname,
        virtualPageTitle: 'Rechercher un club',
        userId: userId,
        licensee: licensee,
        licenseeClub: licenseeClub,
        licenseeFunction: licenseeFunctions,
        newsletterSubscriber: newsletterSubscriber,
        newsletterThematic: newsletterThematic
      },
      dataLayerName: 'dataLayer'
    });
  };

  renderSearched(content: string[]): any {
    return content.map((search, index) => (
      <li key={index}>
        <Link className="label label--rounded label--khaki" to="#">
          {search}
        </Link>
      </li>
    ));
  }

  renderSearchedPracticies(content: Array<{ value: string, label: string }>): any {
    const { pratiques_ok, userPref } = this.props;

    return content
      .filter(pratique => {
        return pratiques_ok.indexOf(pratique.value) > -1;
      })
      .sort(function(a, b) {
        let x = a.value.toLowerCase();
        let y = b.value.toLowerCase();
        return x < y ? -1 : x > y ? 1 : 0;
      })
      .map((search, index) => (
        <li key={index}>
          <button
            className="label label--rounded label--khaki"
            onClick={handleTagEvent(
              'trouver_club_page',
              'filtre rugby',
              search.label,
              userPref,
              this.handleClickPracticies(search.label)
            )}
          >
            {search.label}
          </button>
        </li>
      ));
  }

  renderSearchedClubs(): any {
    return this.props.searchClubs.map((club, index) => (
      <li key={index}>
        <Link className="link-club " to="#">
          <img src={club.logo} alt={club.name} height="32" width="32" />
          {club.name}
        </Link>
      </li>
    ));
  }

  renderSearchedClub(content: string[]): any {
    return content.map((search, index) => (
      <li key={index}>
        <Link className="label label--rounded label--khaki" to="#">
          {search}
        </Link>
      </li>
    ));
  }

  renderForm = () => {
    const classNamesInput = dynamicClassName('search-club');
    this.state.inputFocused && classNamesInput.add('has-autocompletion');
    const practices = this.props.pratiques.map(p => ({ value: p, label: p }));

    return (
      <form action="trouver-un-club-result.html" className={classNamesInput.build()}>
        <h1 className="ft-h2">Trouver un club</h1>
        <p className="banner__desc mb-5 mb-md-4">
          Retrouvez les clubs les plus proches de chez vous, filtrés par pratique, nombre de licenciés, horaires,
          compétitions et apprenez en plus sur votre futur club !
        </p>
        <ClubSearch
          handleInputClicked={this.handleInputClicked}
          handleClickOutside={this.handleClickOutside}
          geolocalisation={this.state.geolocalisation}
          userPref={this.props.userPref}
        />
        <div className="search-club__type">
          <p className="ft-h3 ft-400 mb-2">Types de rugby</p>
          <ul className="list-label mb-2">{this.renderSearchedPracticies(practices)}</ul>
        </div>
        {/*<div className="search-club__city">
          <p className="ft-h3 ft-400 mb-2">Villes les plus recherchées</p>
          <ul className="list-label mb-2">{this.renderSearched(this.props.searchCities)}</ul>
        </div>
        <div className="search-club__club">
          <p className="ft-h3 ft-400 mb-2">Clubs les plus recherchés</p>
          <ul className="list-label mb-2">{this.renderSearchedClubs()}</ul>
        </div>*/}
      </form>
    );
  };

  handleClickPracticies = (value: string) => {
    return () => {
      this.setState({
        redirect: true,
        rugbyTypes: value
      });
    };
  };

  handleInputClicked = () => {
    this.setState({ inputFocused: true });
  };

  handleClickOutside = () => {
    this.setState({ inputFocused: false });
  };

  renderCardThumbnail = () => {
    const { blocs_trouver_club } = this.props;

    return blocs_trouver_club.map((thumbnail, index) => {
      let url = '';
      if (thumbnail.parent_slugs[1]) {
        url = `${thumbnail.parent_slugs[1]}/`;
      }
      if (thumbnail.parent_slugs[2]) {
        url = `${url}${thumbnail.parent_slugs[2]}/`;
      }
      url = `${url}${thumbnail.object_slug}`;
      return (
        <div key={index} className="col col-md-4 mb-2 mb-md-0">
          <CardThumbnail
            url={`/${url}`}
            backgroundUrl={thumbnail.featured_image}
            title={thumbnail.title}
            desc={thumbnail.description}
            linkLabel={thumbnail.texte_cta}
          />
        </div>
      );
    });
  };

  render() {
    if (this.state.redirect)
      return (
        <Redirect
          to={{
            pathname: `/trouver-un-club-de-rugby/resultats-de-recherche/`,
            state: {
              rugbyTypes: this.state.rugbyTypes
            }
          }}
        />
      );
    return (
      <>
        <section className="banner banner--searchClub">
          <div
            className="banner__thumbnail img-green"
            style={{ backgroundImage: `url(${this.props.featured_image})` }}
          />
          <div className="container">
            <ol className="breadcrumb is-dark ft-h6 ft-up ">
              <li>
                <Link to="/" title="Se rendre à la page d'accueil">
                  accueil
                </Link>
              </li>
              <li>
                <span>Trouver un club</span>
              </li>
            </ol>
            {this.renderForm()}
          </div>
        </section>
        <div className="container">
          <div className="row mb-6 mb-md-7">{this.renderCardThumbnail()}</div>
        </div>
      </>
    );
  }
}

export default withHelmet(ClubFinder)((Helmet, pageProps) => {
  const meta = getMetaBySlug(pageProps.location.pathname);
  return (
    <Helmet>
      <title>{meta.title()}</title>
      <meta name={'description'} content={meta.description()} />
      <meta property={'og:title'} content={meta.title()} />
      <meta property={'og:description'} content={meta.description()} />
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
